import { Link } from 'react-router'
import { DATA } from '#app/data/portfolio.tsx'
import { type Route } from './+types/resume.ts'

export const meta: Route.MetaFunction = () => [
	{ title: `${DATA.name} — Resume` },
	{
		name: 'description',
		content: `Resume for ${DATA.name} — AI engineer: agentic harnesses, multi-modal LLM pipelines, and the deterministic engines they reason over.`,
	},
]

// Resume summary — POSITIONING §4 "full" bio. Kept here (not in DATA.summary,
// which is the site's first-person hero copy) because the resume voice differs.
const SUMMARY =
	'Ten years building data-intensive systems, the last four on AI: local multi-modal pipelines, agentic harnesses with evals and guardrails, and the deterministic engines they reason over. Full-stack throughout — the pipelines, the engines, and the surfaces people use.'

function Header() {
	const { name, location } = DATA
	const { email } = DATA.contact
	const links = [
		{ label: 'slvr.mn', href: DATA.url },
		{ label: 'github.com/bespy', href: DATA.contact.social.GitHub.url },
		{ label: 'linkedin.com/in/jacob0x', href: DATA.contact.social.LinkedIn.url },
	]
	return (
		<header className="pb-2">
			<h1 className="text-3xl font-bold print:tracking-normal">{name}</h1>
			<p className="mt-1 text-sm font-medium text-neutral-500">
				AI Engineer — agentic systems, end-to-end
			</p>
			<div className="mt-2 flex flex-wrap gap-x-1.5 gap-y-1 text-xs text-neutral-500">
				<a className="hover:underline" href={`mailto:${email}`}>
					{email}
				</a>
				<span aria-hidden>·</span>
				<a className="hover:underline" href="tel:+972504398118">
					+972 50-439-8118
				</a>
				<span aria-hidden>·</span>
				<span>{location}</span>
				{links.map((l) => (
					<span key={l.href} className="contents">
						<span aria-hidden>·</span>
						<a className="hover:underline" href={l.href}>
							{l.label}
						</a>
					</span>
				))}
			</div>
		</header>
	)
}

// Descriptions in DATA.work use `**bold**` and `` `code` `` markdown (shared with
// the site's markdown-rendered bio). The résumé renders plain text, so parse the
// simple inline markers into <strong>/<code> rather than leaking literal
// asterisks/backticks into the PDF.
function renderInlineBold(text: string) {
	return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<strong key={i} className="font-semibold text-neutral-800">
					{part.slice(2, -2)}
				</strong>
			)
		}
		if (part.startsWith('`') && part.endsWith('`')) {
			return (
				<code
					key={i}
					className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-[0.9em] text-neutral-800"
				>
					{part.slice(1, -1)}
				</code>
			)
		}
		return part
	})
}

function Section({
	title,
	children,
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		// The original problem was a heading orphaned from its content ("Tech Stack" printed
		// its h2 on page 1 and its body on page 2). `break-inside-avoid` on the whole section
		// fixed that but caused a worse one on 2026-08-16: once the Fairway entry grew, the
		// entire Experience section no longer fit on page 1, so it jumped to page 2 and left
		// page 1 90% blank — a 4-page résumé. `break-after-avoid` on the heading solves the
		// orphan directly and lets long sections flow across the page boundary.
		<section className="mt-10 border-t border-neutral-200 pt-6">
			<h2 className="mb-4 text-xs font-semibold tracking-widest text-neutral-500 uppercase print:break-after-avoid">
				{title}
			</h2>
			{children}
		</section>
	)
}

export default function ResumeRoute() {
	return (
		// Always-light document — a résumé prints/exports to PDF, so it must NOT
		// follow the site's dark theme. Full-width white bg so the PDF has no dark
		// margins around the centered content.
		<main className="min-h-dvh bg-white font-sans text-neutral-900">
			<div className="mx-auto max-w-3xl px-6 py-10 print:py-6">
				{/* Toolbar — hidden when printing / in the generated PDF */}
				<div className="mb-6 flex items-center justify-between gap-3 print:hidden">
					<Link
						to="/"
						className="rounded-lg border border-neutral-200 px-4 py-2 text-sm font-medium transition-colors hover:bg-neutral-100"
					>
						← Back to slvr.mn
					</Link>
					<a
						href="/jacob-silverman-resume.pdf"
						className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-700"
					>
						Download PDF ↓
					</a>
				</div>

				<Header />

				<Section title="Summary">
					<p className="text-sm leading-relaxed text-pretty">{SUMMARY}</p>
				</Section>

				<Section title="Experience">
					<div className="space-y-5">
						{DATA.work.map((job) => (
							<div key={`${job.company}-${job.start}`}>
								{/* Keep a role's heading with the start of its body. The rule sits
								    on the heading row, not the whole entry: the current role is
								    taller than a page and has to be allowed to split. */}
								<div className="flex items-baseline justify-between gap-3 print:break-after-avoid">
									<h3 className="text-sm font-semibold">
										{job.title} · {job.company}
									</h3>
									<span className="shrink-0 text-xs text-neutral-500">
										{job.start} – {job.end}
									</span>
								</div>
								<div className="mt-1.5 space-y-2 text-sm leading-relaxed text-neutral-600">
									{job.description.split('\n\n').map((para, i) => (
										<p key={i}>{renderInlineBold(para)}</p>
									))}
								</div>
							</div>
						))}
					</div>
				</Section>

				<Section title="Projects">
					<div className="space-y-4">
						{DATA.projects.map((p) => {
							const blurb = p.resumeBlurb
							return (
								<div key={p.title}>
									<div className="flex items-baseline justify-between gap-3 print:break-after-avoid">
										<h3 className="text-sm font-semibold">{p.title}</h3>
										<span className="shrink-0 text-xs text-neutral-500">{p.dates}</span>
									</div>
									<p className="mt-1 text-sm leading-relaxed text-neutral-600">
										{renderInlineBold(blurb)}
									</p>
								</div>
							)
						})}
					</div>
				</Section>

				<Section title="Education">
					{DATA.education.map((ed) => (
						<div key={ed.school}>
							<div className="flex items-baseline justify-between gap-3">
								<h3 className="text-sm font-semibold">{ed.school}</h3>
								<span className="shrink-0 text-xs text-neutral-500">
									{ed.start} – {ed.end}
								</span>
							</div>
							<p className="mt-0.5 text-sm text-neutral-600">{ed.degree}</p>
						</div>
					))}
				</Section>

				<Section title="Tech Stack">
					<p className="text-sm leading-relaxed">{DATA.skills.join(' · ')}</p>
				</Section>
			</div>
		</main>
	)
}
